import { useActorId, useAxios, useBearer, useProjectId, useServerContext } from '@healthblocks-io/core'
import { AxiosError } from 'axios'
import { useCallback, useMemo } from 'react'
import { v4 as uuidv4 } from 'uuid'

import { mixpanel } from '@/mixpanel'

import type { NavigationRequest, NavigationResponse } from '../types/booking.type'

/**
 * Fetching the next page of dates is a background request with nothing to abort
 * it: the booking flow keeps one page in flight at a time and only clears that
 * lock when the request settles. Without a ceiling a stalled Synops call would
 * leave the slot list unable to paginate for the rest of the session.
 *
 * The ceiling sits above the server's own worst case rather than below it. The
 * Synops SOAP client has no timeout of its own, and the API gives a page two
 * attempts plus a short sleep, so a slow-but-succeeding page can legitimately
 * run past a minute. A tighter budget would abandon work that was going to
 * arrive, which is worse than waiting for it.
 */
const PAGINATION_TIMEOUT_MS = 90_000

/**
 * Whether a navigation is the date carousel asking for the next page, as opposed
 * to the two other requests that travel as 'load-more-slots': refreshing a single
 * date (which sets both tillDate and numberOfDays) and extending the search window
 * (which sets tillDate). Those are user-initiated and wait behind their own
 * feedback, so they are neither deduplicated nor timed out here.
 */
export const isPaginationRequest = (request: NavigationRequest): boolean =>
  request.action === 'load-more-slots' && !request.tillDate && !request.numberOfDays

export const useBooking = () => {
  const axios = useAxios()
  const server = useServerContext()
  const projectId = useProjectId()
  const actorId = useActorId()
  const bearer = useBearer()

  const trackingId = useMemo(() => {
    return uuidv4()
  }, [])

  const startBookingFlow = useCallback(async () => {
    const response = await axios.get<NavigationResponse>('/triage/start')

    if (response.status >= 400) {
      mixpanel.track('booking_flow_error', {
        step: 'start-session',
      })
      throw new Error('Failed to start booking flow')
    }

    mixpanel.track('booking_flow_started')

    return response.data
  }, [axios])

  const navigateBookingFlow = useCallback(
    async (request: NavigationRequest, abortController?: AbortController) => {
      mixpanel.track('booking_flow_step_action', {
        id: trackingId,
        step: request.flowState.currentStep,
        action: request.action,
        withTriage: !!request.flowState.triagePath?.length,
      })

      try {
        const response = await axios.post<NavigationResponse>('/triage/navigate', request, {
          signal: abortController?.signal,
          ...(isPaginationRequest(request) ? { timeout: PAGINATION_TIMEOUT_MS } : {}),
        })

        if (response.status >= 400) {
          mixpanel.track('booking_flow_error', {
            id: trackingId,
            step: request.action,
            withTriage: !!response.data.flowState.triagePath?.length,
          })
          throw new Error('Failed to navigate booking flow')
        }

        // Convention status of the single checked doctor (free CMS text);
        // 'unknown' when no or multiple doctors are selected.
        const checkedDoctors = Object.values(response.data.flowState.filters?.doctors ?? {}).filter(
          (doctor) => doctor.checked,
        )
        const conventionStatus =
          checkedDoctors.length === 1 ? (checkedDoctors[0].conventionStatus?.nl ?? 'unknown') : 'unknown'

        mixpanel.track('booking_flow_step_viewed', {
          id: trackingId,
          step: response.data.currentStep.type,
          withTriage: !!response.data.flowState.triagePath?.length,
          convention_status: conventionStatus,
        })

        return response.data
      } catch (error: unknown) {
        if (error instanceof AxiosError && (error.name === 'CanceledError' || error.message?.includes('canceled'))) {
          return null
        }

        throw error
      }
    },
    [axios],
  )

  /**
   * Tell the API this booking session is being left without a booking, so the
   * slot it may still hold in Synops is released for other patients.
   *
   * Sent with `fetch` + `keepalive` rather than axios: it is fired from the
   * close button, from the flow unmounting and from `pagehide`, and only a
   * keepalive request is allowed to outlive the page. Mirrors the auth and
   * project/actor params that `useAxios` adds to every request.
   */
  const abandonBookingFlow = useCallback(
    async (sessionId: string) => {
      // `server.api` may be relative in some environments; resolve against the page.
      const url = new URL(`${server.api}/triage/abandon`, window.location.origin)
      url.searchParams.set('projectId', String(projectId))
      if (actorId) url.searchParams.set('actorId', String(actorId))

      const response = await fetch(url.toString(), {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json', ...((await bearer()) ?? {}) },
        body: JSON.stringify({ sessionId }),
      })

      mixpanel.track('booking_flow_slot_released', { id: trackingId, sessionId, ok: response.ok })
      return response.ok
    },
    [server.api, projectId, actorId, bearer, trackingId],
  )

  return {
    trackingId,
    startBookingFlow,
    navigateBookingFlow,
    abandonBookingFlow,
  }
}
